import { useState } from "react";
import { ActionBarConfig } from "common/utils/actionBar";
import { ActionKey } from "common/utils/actionBar";
import { ButtonLink } from "../ButtonLink";
import { Icon, IconNames } from "../Icons/LucideIcons";
import { useTableRowsSelectionState } from "../Tables/RowSelectionProvider";
import { Body } from "../Typography";
import {
  ActionBarContainer,
  ActionBarContent,
  ActionBarOption,
  ActionBarSection,
  ActionBarWrapper,
  IconWrapper,
  LoadingWrapper,
  MenuSeparator,
  Options,
  SelectAll,
} from "./__styles__/ActionBar";
import { useActionBarDeleteModal } from "./deleteModal";
import { useActionBarRegenerateAIFieldsHandler } from "./regenerateAIFieldsHandlers";
import { useActionBarVisibilityHandlers } from "./visibilityHandlers";

interface ActionBarProps {
  actionBarConfig: ActionBarConfig;
  onSubmit: () => void;
}

const ACTION_BUTTONS: Record<
  ActionKey,
  { iconName: IconNames; actionName: ActionKey }
> = {
  viewOnMap: {
    iconName: "earth",
    actionName: "viewOnMap",
  },
  visibility: {
    iconName: "eye-off",
    actionName: "visibility",
  },
  regenerateAIFields: {
    iconName: "sparkles",
    actionName: "regenerateAIFields",
  },
  delete: {
    iconName: "trash-2",
    actionName: "delete",
  },
};

type ActionHandlers = {
  showDeleteModal: () => void;
  handleShowToPublic: () => void;
  handleHideFromPublic: () => void;
  handleRegenerateAIFields: () => void;
};

const ACTION_OPTIONS: Record<
  ActionKey,
  (
    handlers: ActionHandlers,
  ) => Array<{ icon: IconNames; label: string; onClick: () => void }>
> = {
  viewOnMap: () => [
    {
      icon: "earth",
      label: "View on map",
      onClick: () => {
        console.log("View on map clicked");
      },
    },
  ],
  visibility: handlers => [
    {
      icon: "eye",
      label: "Show to public",
      onClick: handlers.handleShowToPublic,
    },
    {
      icon: "eye-off",
      label: "Hide from public",
      onClick: handlers.handleHideFromPublic,
    },
  ],
  regenerateAIFields: handlers => [
    {
      icon: "sparkles",
      label: "Regenerate AI fields",
      onClick: handlers.handleRegenerateAIFields,
    },
  ],
  delete: handlers => [
    {
      icon: "trash-2",
      label: "Delete",
      onClick: () => {
        handlers.showDeleteModal();
      },
    },
  ],
};

const ACTION_LOADING_TEXT: Partial<
  Record<ActionKey, (totalSelected: number) => string>
> = {
  delete: (totalSelected: number) =>
    `Deleting ${numberFormatter.format(totalSelected)} ${totalSelected === 1 ? "row" : "rows"}`,
  visibility: (totalSelected: number) =>
    `Changing visibility of ${numberFormatter.format(totalSelected)} ${totalSelected === 1 ? "row" : "rows"}`,
  regenerateAIFields: (totalSelected: number) =>
    `Regenerating AI fields for ${numberFormatter.format(totalSelected)} ${totalSelected === 1 ? "row" : "rows"}`,
};

const numberFormatter = Intl.NumberFormat("en-US");

export const ActionBar = ({ actionBarConfig, onSubmit }: ActionBarProps) => {
  const { actions, objectType } = actionBarConfig;
  const { state, onSelectAllRows, setLoadingAction } =
    useTableRowsSelectionState();
  const { totalSelected, showSelectAllButton, totalCount, loadingAction } =
    state;
  const [activeAction, setActiveAction] = useState<ActionKey | null>(null);

  const showDeleteModal = useActionBarDeleteModal({
    state,
    objectType,
    onDelete: onSubmit,
    setLoadingAction,
  });

  const { handleShowToPublic, handleHideFromPublic } =
    useActionBarVisibilityHandlers({
      state,
      objectType,
      onComplete: onSubmit,
      setLoadingAction,
    });

  const handleRegenerateAIFields = useActionBarRegenerateAIFieldsHandler({
    state,
    objectType,
    onComplete: onSubmit,
    setLoadingAction,
  });

  const toggleActionMenu = (menuType: ActionKey) => {
    setActiveAction(prevActiveAction =>
      prevActiveAction === menuType ? null : menuType,
    );
  };

  const handlers: ActionHandlers = {
    showDeleteModal,
    handleShowToPublic,
    handleHideFromPublic,
    handleRegenerateAIFields,
  };

  return (
    <ActionBarWrapper>
      <ActionBarContainer>
        {loadingAction ?
          <ActionBarContent>
            <ActionBarSection>
              <Body
                size="default"
                type="regular"
                color="contentPrimaryInverted"
              >
                {ACTION_LOADING_TEXT[loadingAction]?.(totalSelected) ??
                  "Loading"}
              </Body>
            </ActionBarSection>
            <LoadingWrapper>
              <Icon
                iconName="loading"
                size={16}
                color="contentDisabled"
                aria-label="loading"
              />
            </LoadingWrapper>
          </ActionBarContent>
        : <>
            {activeAction && (
              <>
                <Options>
                  {ACTION_OPTIONS[activeAction](handlers).map(option => (
                    <ActionBarOption
                      key={option.label}
                      onClick={() => {
                        option.onClick();
                        setActiveAction(null);
                      }}
                    >
                      <Icon
                        iconName={option.icon}
                        size={16}
                        color="contentPrimaryInverted"
                      />
                      <Body
                        size="default"
                        type="regular"
                        color="contentPrimaryInverted"
                      >
                        {option.label}
                      </Body>
                    </ActionBarOption>
                  ))}
                </Options>
                <MenuSeparator />
              </>
            )}

            <ActionBarContent>
              <ActionBarSection>
                <Body
                  size="default"
                  type="regular"
                  color="contentPrimaryInverted"
                >
                  {totalSelected} selected
                </Body>
              </ActionBarSection>

              <ActionBarSection>
                {actions.map(action => {
                  const buttonConfig = ACTION_BUTTONS[action.key];

                  return (
                    <IconWrapper
                      key={action.key}
                      aria-label={`${action.key} action`}
                      onClick={() => toggleActionMenu(buttonConfig.actionName)}
                    >
                      <Icon
                        iconName={buttonConfig.iconName}
                        size={16}
                        color="contentDisabled"
                        hoverColor="contentPrimaryInverted"
                      />
                    </IconWrapper>
                  );
                })}
              </ActionBarSection>
            </ActionBarContent>
          </>
        }
      </ActionBarContainer>

      {showSelectAllButton && (
        <SelectAll>
          <Body size="default" type="regular" color="contentSecondaryInverted">
            We've only selected items visible on this page.
          </Body>
          <ButtonLink
            onClick={() => onSelectAllRows()}
            size="small"
            styleVariant="inverted"
          >
            Select all {numberFormatter.format(totalCount)} items
          </ButtonLink>
        </SelectAll>
      )}
    </ActionBarWrapper>
  );
};
